import { CurrencyDisplay, GameButton, Modal, PixelIcon } from '@/components/ui';
import { useShop } from '@/contexts/ShopContext';
import { useUpgrades } from '@/hooks/useUpgrades';
import { motion } from 'motion/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface UpgradesScreenProps {
  onGoHome: () => void;
  onOpenShop: () => void;
}

function formatCurrencyLabel(currency: 'coins' | 'gems') {
  return currency === 'coins' ? 'Coins' : 'Gems';
}

const UpgradesScreen: React.FC<UpgradesScreenProps> = ({ onGoHome, onOpenShop }) => {
  const { shopState, shopManager } = useShop();
  const { defs, manager } = useUpgrades();
  const navigate = useNavigate();

  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };
  const maxLives = shopManager?.getMaxLives?.() ?? 5;

  return (
    <Modal
      isOpen={true}
      onClose={onGoHome}
      title="Upgrades"
      subtitle="Permanent power. No timers. No excuses."
      variant="success"
      size="fullscreen"
      className="upgrades-modal menu-facelift-modal"
      showCloseButton={true}
      headerContent={
        <div className="menu-toolbar">
          <CurrencyDisplay
            coins={shopState.coins}
            gems={shopState.gems}
            lives={shopState.lives}
            streak={streakStatus.streak}
            canClaimStreak={streakStatus.canClaim}
            variant="header"
            showStreak={true}
            animated={true}
          />
          <p className="menu-toolbar__meta">Max lives: <strong>{maxLives}</strong></p>
          <div className="menu-toolbar__actions">
            <GameButton
              onClick={() => navigate('/worlds')}
              variant="secondary"
              size="small"
              icon="worlds"
              text="Worlds"
              className="menu-toolbar__button"
            />
            <GameButton
              onClick={onOpenShop}
              variant="warning"
              size="small"
              icon="shop"
              text="Shop"
              className="menu-toolbar__button"
            />
          </div>
        </div>
      }
    >
      <div className="menu-card-grid upgrade-card-grid">
        {defs.map((def, idx) => {
          const level = manager.getLevel(def.id);
          const nextCost = manager.getCostForNextLevel(def.id);
          const isMax = level >= def.maxLevel;

          const canAfford =
            def.currency === 'coins' ? shopState.coins >= (nextCost || Infinity) : shopState.gems >= (nextCost || Infinity);

          const effectLabel = (() => {
            if (def.effect.kind === 'propEffect') {
              const active = def.effect.descriptionAtLevel(Math.max(1, level));
              if (level <= 0) return `Unlock: ${def.effect.descriptionAtLevel(1)}`;
              return `Active: ${active}`;
            }
            const bonus = (def.effect.bonusPerLevel || 0) * level;
            if (level <= 0) return `Unlock: +${def.effect.bonusPerLevel} max lives`;
            return `Active: +${bonus} max lives`;
          })();

          return (
            <motion.article
              key={def.id}
              className={`upgrade-card ${isMax ? 'is-maxed' : ''}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04, duration: 0.28 }}
              whileHover={{ y: -2, scale: 1.01 }}
            >
              <div className="upgrade-card__header">
                <div className="upgrade-card__title-wrap">
                  <div className="upgrade-card__icon">
                    <PixelIcon name={def.icon} size={22} />
                  </div>
                  <div>
                    <p className="upgrade-card__title">{def.name}</p>
                    <p className="upgrade-card__subtitle">{def.description}</p>
                  </div>
                </div>

                <div className="upgrade-card__currency-chip">
                  <PixelIcon name={def.currency === 'coins' ? 'coin' : 'gem'} size={14} />
                  <span>{formatCurrencyLabel(def.currency)}</span>
                </div>
              </div>

              <div className="upgrade-card__stats">
                <p>
                  Level: <strong>{level}</strong> / {def.maxLevel}
                </p>
                <p>{effectLabel}</p>
              </div>

              <div className="upgrade-card__actions">
                <GameButton
                  onClick={() => manager.purchase(def.id, shopManager)}
                  disabled={isMax || !canAfford || nextCost === null}
                  variant={isMax ? 'secondary' : canAfford ? 'success' : 'warning'}
                  size="small"
                  icon="upgrades"
                  text={isMax ? 'Maxed' : `Buy (${nextCost} ${def.currency})`}
                  className="upgrade-card__button"
                />
              </div>
            </motion.article>
          );
        })}
      </div>
    </Modal>
  );
};

export default UpgradesScreen;
